import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Menu,
  X,
  Play,
  Video,
  FileText,
  TrendingUp,
  Instagram,
  Youtube,
  Mail,
  MapPin,
  ArrowRight,
  Check,
  Send,
  Crosshair,
  Clapperboard,
  ShieldCheck,
  Zap,
  Activity,
  Goal,
  Globe,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { LanguageProvider, useT } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ca977a4e-b3b7-4d27-b4a9-f442a8131d00/artifacts/vdeibp6k_709755027_17891183232507243_3587188577389135739_n.jpg";
const HERO_BG =
  "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHN0YWRpdW0lMjBsaWdodHMlMjBkYXJrfGVufDB8fHx8MTc4MTQwOTgzM3ww&ixlib=rb-4.1.0&q=85";
const SPOTLIGHT_TEXTURE =
  "https://images.unsplash.com/photo-1690321607902-d97c4b909a23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsdWUlMjBuZW9uJTIwbGlnaHQlMjBkYXJrfGVufDB8fHx8MTc4MTQ0NjM4MHww&ixlib=rb-4.1.0&q=85";

const YOUTUBE_CHANNEL = "https://www.youtube.com/@PlayerSpotlight-1";
const INSTAGRAM_URL = "https://www.instagram.com/playerspotlight.ps";
const INSTAGRAM_HANDLE = "@playerspotlight.ps";
const EMAIL = "playerspotlightps@gmail.com";

/*
 * Portfolio: drop real YouTube video IDs (11-char from watch URL) into the `id` field
 * in /app/frontend/src/lib/i18n.jsx (under portfolio.videos[i].id) once you have them.
 * Any item without an `id` will render a stylized placeholder linking to the channel.
 */

const SERVICE_ICONS = [Video, FileText];
const REASON_ICONS = [Activity, Clapperboard, Crosshair, TrendingUp, Zap, MapPin];

// ---------------- Language Switcher ----------------
const LanguageSwitcher = ({ className = "" }) => {
  const { lang, setLang, t } = useT();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="lang-switcher"
          aria-label={t.lang_label}
          className={`inline-flex items-center gap-2 px-3 h-10 rounded-full border border-white/20 bg-white/[0.03] text-white/80 hover:text-white hover:border-primary/40 transition-colors text-[11px] uppercase tracking-[0.25em] ${className}`}
        >
          <Globe className="h-3.5 w-3.5" strokeWidth={1.5} />
          {lang === "en" ? "EN" : "ΕΛ"}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[hsl(222,40%,13%)] border-white/20 text-white min-w-[140px]"
      >
        <DropdownMenuItem
          data-testid="lang-option-en"
          onClick={() => setLang("en")}
          className={`uppercase tracking-[0.2em] text-xs cursor-pointer ${lang === "en" ? "text-primary" : ""}`}
        >
          English
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="lang-option-el"
          onClick={() => setLang("el")}
          className={`uppercase tracking-[0.2em] text-xs cursor-pointer ${lang === "el" ? "text-primary" : ""}`}
        >
          Ελληνικά
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ---------------- Navigation ----------------
const Navigation = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t.nav.home, href: "#home", key: "home" },
    { label: t.nav.about, href: "#about", key: "about" },
    { label: t.nav.services, href: "#services", key: "services" },
    { label: t.nav.portfolio, href: "#portfolio", key: "portfolio" },
    { label: t.nav.process, href: "#process", key: "process" },
    { label: t.nav.contact, href: "#contact", key: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass" : "bg-transparent"}`}
    >
      <nav className="container mx-auto max-w-7xl px-6 md:px-10 h-20 flex items-center justify-between">
        <a
          data-testid="nav-logo"
          href="#home"
          className="flex items-center gap-3 group"
          aria-label="PlayerSpotlight — Home"
        >
          <span className="relative inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white p-1.5 shadow-[0_8px_30px_-6px_rgba(59,130,246,0.55)] ring-1 ring-white/30 transition-transform duration-300 group-hover:scale-105">
            <img src={LOGO_URL} alt="PlayerSpotlight" className="w-full h-full object-contain" />
          </span>
          <span className="flex items-baseline gap-[0.18em]">
            <span className="font-display text-white text-[15px] sm:text-base tracking-[0.32em] uppercase font-semibold">
              Player
            </span>
            <span className="font-display text-primary text-[15px] sm:text-base tracking-[0.32em] uppercase font-semibold">
              Spotlight
            </span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((link) => (
            <a
              key={link.key}
              data-testid={`nav-link-${link.key}`}
              href={link.href}
              className="text-sm uppercase tracking-[0.18em] text-white/85 hover:text-white transition-colors"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <Button
            asChild
            data-testid="nav-cta-work-with-us"
            className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 h-11 uppercase tracking-[0.18em] text-xs font-semibold"
          >
            <a href="#contact">
              {t.nav.cta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <button
            data-testid="nav-mobile-toggle"
            className="text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden glass border-t border-white/12">
          <div className="container mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((link) => (
              <a
                key={link.key}
                data-testid={`mobile-nav-link-${link.key}`}
                href={link.href}
                onClick={() => setOpen(false)}
                className="text-white/80 uppercase tracking-[0.18em] text-sm py-2 border-b border-white/12"
              >
                {link.label}
              </a>
            ))}
            <Button
              asChild
              data-testid="mobile-nav-cta"
              className="bg-primary hover:bg-primary/90 text-white rounded-full h-12 mt-2"
            >
              <a href="#contact" onClick={() => setOpen(false)}>{t.nav.cta}</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

// ---------------- Hero ----------------
const Hero = () => {
  const { t } = useT();
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-[100vh] flex items-center justify-center overflow-hidden grain">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[hsl(222_30%_6%)]" aria-hidden="true" />
      <div className="absolute inset-0 pitch-lines opacity-60" aria-hidden="true" />
      <div className="absolute inset-0 spotlight-radial" aria-hidden="true" />

      <div className="relative z-10 container mx-auto max-w-6xl px-6 md:px-10 text-center">
        <div className="inline-flex items-center gap-4 mb-10 ps-fade-up">
          <span className="h-px w-12 bg-gradient-to-r from-transparent to-white/60" />
          <span className="text-[10px] tracking-[0.4em] uppercase text-white/75">{t.hero.ornament}</span>
          <span className="h-px w-12 bg-gradient-to-l from-transparent to-white/60" />
        </div>

        <h1 data-testid="hero-title" className="font-display uppercase text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-white ps-fade-up ps-delay-1 max-w-5xl mx-auto">
          {t.hero.title}
        </h1>

        <p data-testid="hero-subtitle" className="mt-8 text-base sm:text-lg md:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed ps-fade-up ps-delay-2">
          {t.hero.subtitle}
        </p>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 ps-fade-up ps-delay-3">
          <Button asChild data-testid="hero-cta-view-work" className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 h-14 uppercase tracking-[0.2em] text-xs font-semibold min-w-[200px]">
            <a href="#portfolio">{t.hero.cta_view}<ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
          <Button asChild variant="outline" data-testid="hero-cta-contact-us" className="border-white/30 bg-transparent hover:bg-white/[0.07] text-white rounded-full px-8 h-14 uppercase tracking-[0.2em] text-xs font-semibold min-w-[200px]">
            <a href="#contact">{t.hero.cta_contact}</a>
          </Button>
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto ps-fade-up ps-delay-4">
          {t.hero.pillars.map((p, i) => {
            const Icon = [Video, FileText, TrendingUp][i];
            return (
              <div
                key={p.title}
                data-testid={`hero-pillar-${i}`}
                className="flex items-center gap-4 text-left p-4 rounded-xl border border-white/12 bg-white/[0.04] backdrop-blur-sm cursor-default"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 text-primary shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <div className="min-w-0">
                  <div className="font-display uppercase text-white text-sm tracking-[0.18em] leading-tight">
                    {p.title}
                  </div>
                  <div className="text-[11px] text-white/65 mt-1 leading-snug">{p.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/55 text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</div>
    </section>
  );
};

// ---------------- About ----------------
const About = () => {
  const { t } = useT();
  const features = [
    { icon: ShieldCheck, text: t.about.f1 },
    { icon: Clapperboard, text: t.about.f2 },
    { icon: Activity, text: t.about.f3 },
    { icon: Goal, text: t.about.f4 },
  ];
  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-36 overflow-hidden">
      <div className="absolute inset-0 spotlight-radial-bottom opacity-50" aria-hidden="true" />
      <div className="container mx-auto max-w-7xl px-6 md:px-10 relative">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="text-primary uppercase tracking-[0.3em] text-xs mb-4 flex items-center gap-2">
              <Crosshair className="h-4 w-4" strokeWidth={1.5} />
              {t.about.kicker}
            </div>
            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[0.95]">
              {t.about.title_1}<br /><span className="text-white/55">{t.about.title_2}</span><br /><span className="text-primary">{t.about.title_3}</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-6">
            <p className="text-lg md:text-xl text-white/85 leading-relaxed">{t.about.p1}</p>
            <p className="mt-6 text-base md:text-lg text-white/75 leading-relaxed">{t.about.p2}</p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {features.map((it) => (
                <div key={it.text} className="flex items-center gap-3 p-4 rounded-lg border border-white/20 bg-white/[0.04]">
                  <it.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <span className="text-sm text-white/80">{it.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------- Services ----------------
const Services = () => {
  const { t } = useT();
  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-36 bg-[hsl(222,40%,10%)] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.08] mix-blend-screen" style={{ backgroundImage: `url(${SPOTLIGHT_TEXTURE})`, backgroundSize: "cover", backgroundPosition: "center" }} aria-hidden="true" />
      <div className="container mx-auto max-w-7xl px-6 md:px-10 relative">
        <div className="max-w-3xl mb-16">
          <div className="text-primary uppercase tracking-[0.3em] text-xs mb-4">{t.services.kicker}</div>
          <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[0.95]">{t.services.title}</h2>
          <p className="mt-6 text-lg text-white/75 max-w-2xl">{t.services.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {t.services.items.map((s, i) => {
            const Icon = SERVICE_ICONS[i] || Video;
            return (
              <div key={s.title} data-testid={`service-card-${i}`} className="group relative p-8 md:p-12 rounded-2xl bg-[hsl(222,40%,13%)] border border-white/12 overflow-hidden transition-all duration-300 hover:border-primary/40 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(37,99,235,0.4)] flex flex-col">
                <a href="#contact" aria-label={`${s.title} — ${t.services.cta}`} className="absolute inset-0 z-10" data-testid={`service-card-link-${i}`}>
                  <span className="sr-only">{t.services.cta}</span>
                </a>
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                <div className="relative flex-1 pointer-events-none">
                  <div className="font-display text-white/10 text-7xl mb-6 leading-none">0{i + 1}</div>
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 mb-6">
                    <Icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display uppercase text-2xl md:text-3xl text-white tracking-tight mb-4">{s.title}</h3>
                  <p className="text-white/75 leading-relaxed">{s.desc}</p>
                  <ul className="mt-6 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-3 text-sm text-white/85">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" strokeWidth={2} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative mt-8 pt-6 border-t border-white/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pointer-events-none">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 mb-1">Price</div>
                    <div className="font-display uppercase text-white text-lg tracking-wide">{t.services.price_label}</div>
                  </div>
                  <Button asChild data-testid={`service-cta-${i}`} className="w-full sm:w-auto bg-primary group-hover:bg-primary/90 text-white rounded-full px-5 h-11 uppercase tracking-[0.14em] text-[11px] font-semibold whitespace-nowrap pointer-events-auto relative z-20">
                    <a href="#contact">
                      {t.services.cta}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------------- Portfolio ----------------
const VideoCard = ({ video, index, t }) => {
  const hasId = !!video.id;
  return (
    <div data-testid={`portfolio-card-${index}`} className="group relative rounded-2xl overflow-hidden border border-white/12 bg-[hsl(222,40%,12%)] transition-all hover:border-primary/40 hover:-translate-y-1 flex flex-col">
      <div className="relative aspect-video bg-black">
        {hasId ? (
          <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${video.id}`} title={video.player} allow="accelerated-encoding; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        ) : (
          <a href={YOUTUBE_CHANNEL} target="_blank" rel="noreferrer noopener" className="absolute inset-0 flex items-center justify-center" data-testid={`portfolio-placeholder-link-${index}`}>
            <div className="absolute inset-0 pitch-lines opacity-50" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(37,99,235,0.18), transparent 60%)" }} />
            <div className="relative z-10 flex flex-col items-center gap-3">
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white shadow-[0_0_40px_-8px_rgba(37,99,235,0.8)] transition-transform group-hover:scale-110">
                <Play className="h-6 w-6 ml-1" fill="currentColor" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/75">{t.portfolio.placeholder}</span>
            </div>
          </a>
        )}
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur border border-white/20 text-[10px] uppercase tracking-[0.2em] text-primary">
          {video.service}
        </span>
      </div>
      <div className="p-5 flex-1 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h4 className="font-display uppercase text-white text-lg leading-tight truncate">{video.player}</h4>
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/65 mt-1">
            {t.portfolio.labels.position}: <span className="text-white/85">{video.position}</span>
          </div>
        </div>
        <a
          href={hasId ? `https://www.youtube.com/watch?v=${video.id}` : YOUTUBE_CHANNEL}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`portfolio-watch-${index}`}
          className="shrink-0 inline-flex items-center gap-2 px-4 h-9 rounded-full border border-primary/40 text-primary text-[10px] uppercase tracking-[0.22em] font-medium hover:bg-primary hover:text-white transition-colors"
        >
          <Play className="h-3 w-3" fill="currentColor" />
          {t.portfolio.watch}
        </a>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const { t } = useT();
  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-24 md:py-36">
      <div className="container mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-primary uppercase tracking-[0.3em] text-xs mb-4">{t.portfolio.kicker}</div>
            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[0.95]">{t.portfolio.title}</h2>
            <p className="mt-6 text-lg text-white/75 max-w-2xl">{t.portfolio.subtitle}</p>
          </div>
          <Button asChild variant="outline" data-testid="portfolio-channel-cta" className="border-white/20 bg-transparent hover:bg-white/[0.07] text-white rounded-full px-6 h-12 uppercase tracking-[0.18em] text-xs">
            <a href={YOUTUBE_CHANNEL} target="_blank" rel="noreferrer noopener">
              <Youtube className="mr-2 h-4 w-4" />
              {t.portfolio.cta}
            </a>
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.portfolio.videos.map((v, i) => (
            <VideoCard key={i} video={v} index={i} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- Who It's For ----------------
const WhyChoose = () => {
  const { t } = useT();
  const AUDIENCE_ICONS = [Goal, Crosshair];
  return (
    <section id="why" data-testid="why-section" className="relative py-24 md:py-36 bg-[hsl(222,40%,10%)] overflow-hidden">
      <div className="absolute inset-0 spotlight-radial-bottom opacity-40" aria-hidden="true" />
      <div className="container mx-auto max-w-7xl px-6 md:px-10 relative">
        <div className="max-w-3xl mb-16">
          <div className="text-primary uppercase tracking-[0.3em] text-xs mb-4">{t.why.kicker}</div>
          <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[0.95]">
            {t.why.title_1}
          </h2>
          <p className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed">{t.why.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {t.why.audiences.map((a, i) => {
            const Icon = AUDIENCE_ICONS[i] || Goal;
            return (
              <div
                key={a.tag}
                data-testid={`audience-card-${i}`}
                className="group relative overflow-hidden p-8 md:p-12 rounded-2xl bg-[hsl(222,40%,13%)] border border-white/15 transition-all duration-300 hover:border-primary/50 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(59,130,246,0.45)]"
              >
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-primary/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-8">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/15 border border-primary/30 text-primary">
                      <Icon className="h-6 w-6" strokeWidth={1.6} />
                    </span>
                    <span className="font-display uppercase text-[11px] tracking-[0.3em] text-primary">
                      {a.tag}
                    </span>
                  </div>
                  <h3 className="font-display uppercase text-3xl md:text-4xl text-white tracking-tight leading-[1.05] mb-4">
                    {a.title}
                  </h3>
                  <p className="text-white/85 leading-relaxed">{a.desc}</p>
                  <ul className="mt-8 space-y-3">
                    {a.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-white/85">
                        <span className="mt-[7px] inline-block w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------------- Process ----------------
const Process = () => {
  const { t } = useT();
  return (
    <section id="process" data-testid="process-section" className="relative py-24 md:py-36 overflow-hidden">
      <div className="absolute inset-0 pitch-lines opacity-30" aria-hidden="true" />
      <div className="container mx-auto max-w-7xl px-6 md:px-10 relative">
        <div className="max-w-3xl mb-16">
          <div className="text-primary uppercase tracking-[0.3em] text-xs mb-4">{t.process.kicker}</div>
          <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[0.95]">{t.process.title}</h2>
          <p className="mt-6 text-lg text-white/75">{t.process.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 md:gap-4">
          {t.process.steps.map((s, i) => (
            <div key={s.title} data-testid={`process-step-${i}`} className="relative p-8 rounded-2xl border border-white/20 bg-[hsl(222,40%,12%)]">
              {i < t.process.steps.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary/50 z-10" strokeWidth={1.5} />
              )}
              <div className="font-display text-7xl text-primary/15 leading-none mb-4">0{i + 1}</div>
              <h3 className="font-display uppercase text-white text-lg md:text-xl tracking-tight mb-3 leading-tight">{s.title}</h3>
              <p className="text-sm text-white/75 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- Contact ----------------
const ContactForm = () => {
  const { t } = useT();
  const [form, setForm] = useState({
    name: "",
    email: "",
    contact_handle: "",
    player_position: "",
    interested_in: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.interested_in) {
      toast.error(t.contact.form.error_required);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", contact_handle: "", player_position: "", interested_in: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.[0]?.msg;
      toast.error(typeof detail === "string" ? detail : t.contact.form.error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  const interestOptions = [
    { value: "highlight", label: t.contact.form.interest_options.highlight },
    { value: "analysis", label: t.contact.form.interest_options.analysis },
    { value: "both", label: t.contact.form.interest_options.both },
  ];

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-5 p-8 md:p-10 rounded-2xl border border-white/20 bg-[hsl(222,40%,11%)]">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] text-white/75">{t.contact.form.name}</Label>
          <Input id="name" data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder={t.contact.form.name_ph} className="mt-2 h-12 bg-transparent border-white/20 focus:border-primary text-white placeholder:text-white/45" required />
        </div>
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-white/75">{t.contact.form.email}</Label>
          <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={update("email")} placeholder={t.contact.form.email_ph} className="mt-2 h-12 bg-transparent border-white/20 focus:border-primary text-white placeholder:text-white/45" required />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="contact_handle" className="text-xs uppercase tracking-[0.2em] text-white/75">{t.contact.form.contact_handle}</Label>
          <Input id="contact_handle" data-testid="contact-input-handle" value={form.contact_handle} onChange={update("contact_handle")} placeholder={t.contact.form.contact_handle_ph} className="mt-2 h-12 bg-transparent border-white/20 focus:border-primary text-white placeholder:text-white/45" />
        </div>
        <div>
          <Label htmlFor="position" className="text-xs uppercase tracking-[0.2em] text-white/75">{t.contact.form.position}</Label>
          <Input id="position" data-testid="contact-input-position" value={form.player_position} onChange={update("player_position")} placeholder={t.contact.form.position_ph} className="mt-2 h-12 bg-transparent border-white/20 focus:border-primary text-white placeholder:text-white/45" />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.2em] text-white/75">{t.contact.form.interest}</Label>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup">
          {interestOptions.map((opt) => {
            const active = form.interested_in === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`contact-interest-${opt.value}`}
                onClick={() => setForm({ ...form, interested_in: opt.value })}
                className={`h-12 px-4 rounded-lg border text-xs uppercase tracking-[0.18em] font-medium transition-all ${
                  active
                    ? "border-primary bg-primary/15 text-white shadow-[0_0_0_1px_hsl(221_83%_53%_/_0.3)]"
                    : "border-white/20 bg-transparent text-white/85 hover:border-white/30 hover:text-white"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label htmlFor="message" className="text-xs uppercase tracking-[0.2em] text-white/75">{t.contact.form.message}</Label>
        <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={update("message")} rows={5} placeholder={t.contact.form.message_ph} className="mt-2 bg-transparent border-white/20 focus:border-primary text-white placeholder:text-white/45 resize-none" required />
      </div>

      <Button type="submit" data-testid="contact-submit-btn" disabled={submitting} className="w-full h-14 bg-primary hover:bg-primary/90 text-white rounded-full uppercase tracking-[0.2em] text-xs font-semibold">
        {submitting ? t.contact.form.submitting : (<>{t.contact.form.submit}<Send className="ml-2 h-4 w-4" /></>)}
      </Button>
    </form>
  );
};

const Contact = () => {
  const { t } = useT();
  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-36 bg-[hsl(222,40%,10%)] overflow-hidden">
      <div className="absolute inset-0 spotlight-radial opacity-60" aria-hidden="true" />
      <div className="container mx-auto max-w-7xl px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-primary uppercase tracking-[0.3em] text-xs mb-4">{t.contact.kicker}</div>
            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[0.95]">
              {t.contact.title_1} <span className="text-primary">{t.contact.title_2}</span>
            </h2>
            <p className="mt-6 text-lg text-white/75 max-w-md">{t.contact.subtitle}</p>

            <div className="mt-10 space-y-5">
              <a data-testid="contact-instagram" href={INSTAGRAM_URL} target="_blank" rel="noreferrer noopener" className="group flex items-center gap-4 p-5 rounded-xl border border-white/20 bg-white/[0.04] hover:border-primary/40 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <Instagram className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 mb-1">{t.contact.labels.instagram}</div>
                  <div className="text-white font-medium">{INSTAGRAM_HANDLE}</div>
                </div>
              </a>
              <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="group flex items-center gap-4 p-5 rounded-xl border border-white/20 bg-white/[0.04] hover:border-primary/40 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 mb-1">{t.contact.labels.email}</div>
                  <div className="text-white font-medium break-all">{EMAIL}</div>
                </div>
              </a>
              <a data-testid="contact-youtube" href={YOUTUBE_CHANNEL} target="_blank" rel="noreferrer noopener" className="group flex items-center gap-4 p-5 rounded-xl border border-white/20 bg-white/[0.04] hover:border-primary/40 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <Youtube className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 mb-1">{t.contact.labels.youtube}</div>
                  <div className="text-white font-medium">@PlayerSpotlight-1</div>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------- Footer ----------------
const Footer = () => {
  const { t } = useT();
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/12 bg-[hsl(222_35%_5%)] py-14">
      <div className="container mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-md bg-white p-1.5 shrink-0">
              <img src={LOGO_URL} alt="PlayerSpotlight" className="w-full h-full object-contain" />
            </span>
            <div>
              <div className="font-display uppercase text-white text-xl tracking-wide">
                Player<span className="text-primary">Spotlight</span>
              </div>
              <div className="text-sm text-white/75 mt-1 max-w-md leading-relaxed">
                {t.footer.tagline}
              </div>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="footer-instagram-link"
                className="inline-flex items-center gap-2 mt-3 text-sm text-white/85 hover:text-primary transition-colors"
              >
                <Instagram className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-white/55">Instagram:</span>
                <span className="font-medium">{INSTAGRAM_HANDLE}</span>
              </a>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer noopener" data-testid="footer-instagram" className="w-10 h-10 rounded-full border border-white/20 hover:border-primary/40 flex items-center justify-center text-white/85 hover:text-white transition-colors" aria-label="Instagram"><Instagram className="h-4 w-4" /></a>
            <a href={YOUTUBE_CHANNEL} target="_blank" rel="noreferrer noopener" data-testid="footer-youtube" className="w-10 h-10 rounded-full border border-white/20 hover:border-primary/40 flex items-center justify-center text-white/85 hover:text-white transition-colors" aria-label="YouTube"><Youtube className="h-4 w-4" /></a>
            <a href={`mailto:${EMAIL}`} data-testid="footer-email" className="w-10 h-10 rounded-full border border-white/20 hover:border-primary/40 flex items-center justify-center text-white/85 hover:text-white transition-colors" aria-label="Email"><Mail className="h-4 w-4" /></a>
          </div>
        </div>

        <Separator className="my-8 bg-white/[0.07]" />

        <div className="text-[11px] uppercase tracking-[0.25em] text-white/55 text-center md:text-left">
          © {new Date().getFullYear()} PlayerSpotlight · {t.footer.rights}
        </div>
      </div>
    </footer>
  );
};

// ---------------- Page ----------------
const HomePage = () => (
  <div className="App relative overflow-x-hidden">
    <Navigation />
    <main>
      <Hero />
      <About />
      <Services />
      <Portfolio />
      <WhyChoose />
      <Process />
      <Contact />
    </main>
    <Footer />
    <Toaster
      richColors
      theme="dark"
      position="top-right"
      toastOptions={{
        style: {
          background: "hsl(222 47% 9%)",
          color: "white",
          border: "1px solid hsl(222 47% 18%)",
        },
      }}
    />
  </div>
);

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
